import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext, useEffect } from 'react';

import { PreviewContext } from '../../context/PreviewContext';
import { camelCase } from '../../utils/camel-case';
import { Accordion } from '../accordion';
import { BannerVariant } from '../banner-variant';
import { Cards } from '../cards';
import { ContentAnchorMenu } from '../contentAnchorMenu';
import { Greenhouse } from '../greenhouse';
import { Hero } from '../hero';
import { ImageGridWithColumns } from '../imageGridWithColumns';
import { ImageSlider } from '../imageSlider';
import { Iterable } from '../iterable';
import { LearnMore } from '../learnMore';
import { LockingScroll } from '../lockingScroll';
import { MarketoForm } from '../marketo-form';
import { Marquee } from '../marquee';
import { Pricing } from '../pricing';
import { ProviderSearch } from '../provider-search';
import { SelfStudiesPodcast } from '../self-studies-podcast';
import { Stacks } from '../stacks';
import { TabbedContent } from '../tabbed-content';

export function ModuleAbTest({
	_rawControlModule,
	_rawVariantModule,
	controlModule,
	flagKey,
	moduleAbTestActive,
	variantModule,
}) {
	// hooks
	const flags = useFlags();

	const { setModuleAbTestActive, setFlagName, setFlagValue } =
		useContext(PreviewContext);

	// get ab test data
	const abTestKey = camelCase(flagKey);
	// abTestValue will return control or variant based on the flagKey
	const abTestValue = flags[abTestKey];

	// use either the control or variant module data based on the ab test value
	const moduleAbTestData =
		abTestValue === 'Variant' ? variantModule : controlModule;

	// TODO: note that this will only work if there is only one module level A/B test running on the page
	// If there are multiple module level A/B tests, they will overwrite the context
	useEffect(() => {
		setModuleAbTestActive(true);
		setFlagName(abTestKey);
		setFlagValue(abTestValue);
	}, [abTestValue]);

	// use either the control or variant raw module data based on the ab test value
	// TODO: when we write queries for all modules, we can remove this
	const rawModuleAbTestData =
		abTestValue === 'Variant' ? _rawVariantModule : _rawControlModule;

	// check if the module ab test is active
	const moduleData = moduleAbTestActive ? moduleAbTestData : controlModule;
	const rawModuleData = moduleAbTestActive
		? rawModuleAbTestData
		: _rawControlModule;

	const content = moduleData.map((module) => {
		const { _key, _type } = module;

		const rawGreenhouseData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawIterableData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawLearnMoreData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawMarketoData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawMarqueeData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawProviderSearchData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawPricingData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawSelfStudiesPodcastData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawSliderData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		const rawTabbedContentData = rawModuleData.find(
			(rawModule) => rawModule._key === _key
		);

		switch (_type) {
			case 'stacks':
				return <Stacks {...module} key={_key} />;
			case 'cards':
				return <Cards key={_key} {...module} />;
			case 'accordion':
				return <Accordion key={_key} {...module} />;
			case 'contentAnchorMenuModule':
				return <ContentAnchorMenu key={_key} {...module} />;
			case 'greenhouse':
				return <Greenhouse key={_key} {...rawGreenhouseData} />;
			case 'hero':
				return <Hero key={_key} {...module} />;
			case 'iterableContainer':
				return <Iterable key={_key} {...rawIterableData} />;
			case 'imageGridWithColumns':
				return <ImageGridWithColumns key={_key} {...module} />;
			case 'lockingScrollModule':
				return <LockingScroll key={_key} {...module} />;
			case 'learnMore':
				return <LearnMore key={_key} {...rawLearnMoreData} />;
			case 'marketoContainer':
				return <MarketoForm key={_key} {...rawMarketoData} />;
			case 'marqueeContainer':
				return <Marquee key={_key} {...rawMarqueeData} />;
			case 'providerSearchContainer':
				return <ProviderSearch {...rawProviderSearchData} key={_key} />;
			case 'pricingContainer':
				return <Pricing key={_key} {...rawPricingData} />;
			case 'selfStudiesPodcast':
				return (
					<SelfStudiesPodcast
						key={_key}
						{...rawSelfStudiesPodcastData}
					/>
				);
			case 'slider':
				return <ImageSlider key={_key} {...rawSliderData} />;
			case 'tabs':
				return <TabbedContent key={_key} {...rawTabbedContentData} />;
			case 'bannerVariant':
				return <BannerVariant key={_key} {...module} />;
			default:
				return null;
		}
	});

	return content;
}
